import React, { useState, useCallback } from "react";
import { Search, Package, Truck, AlertCircle, Loader2, ShoppingCart, CheckCircle } from "lucide-react";
import { calculatePrice, formatINR } from "../utils/pricingEngine";

const API_BASE = "https://api.oratorhype.com";
// const API_BASE = "http://localhost:3000";

const ProductLinkPricing = ({ onAddToCart }) => {
  const [productData, setProductData] = useState(null);
  const [url, setUrl] = useState("");
  const [qty, setQty] = useState(1);
  const [deliveryType, setDeliveryType] = useState("normal");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [added, setAdded] = useState(false);

  // ─── Derived pricing ────────────────────────────────────────────────────────
  // productData.price      = Amazon price WITH GST    (for classification + strikethrough)
  // productData.priceExclGST = Amazon price excl. GST (our display/quote base)
  // If backend only returns one price, derive excl-GST as price / 1.18
  const pricing = productData
    ? calculatePrice(
        productData.priceExclGST ?? Math.round(productData.price / 1.18),  // basePriceExclGST
        productData.price,                                                   // amazonPriceWithGST
        productData.weight,
        qty,
        deliveryType
      )
    : null;

  const fetchProduct = useCallback(async () => {
    if (!url.trim()) {
      setError("Please paste a product URL first.");
      return;
    }
    setError("");
    setProductData(null);
    setAdded(false);
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/product/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim() }),
      });
      const result = await response.json();
      if (!result.success) {
        setError(result.error || "Failed to fetch product.");
        return;
      }
      setProductData(result.data);
    } catch {
      setError("Could not connect to backend. Make sure it is running on port 3000.");
    } finally {
      setLoading(false);
    }
  }, [url]);

  const handleAddToCart = () => {
    if (!productData || !onAddToCart) return;
    onAddToCart(
      {
        id: `url-${Date.now()}`,
        name: productData.name,
        brand: productData.brand,
        model: productData.model,
        // Both prices passed to cart so PricingTool & QuotationPreview can use them
        amazonPrice: productData.price,                                         // WITH GST
        priceExclGST: productData.priceExclGST ?? Math.round(productData.price / 1.18), // excl. GST
        weight: productData.weight,
        imageUrl: productData.imageUrl,
        source: productData.source,
      },
      qty,
      deliveryType
    );
    setAdded(true);
    setTimeout(() => setAdded(false), 3000);
  };

  return (
    <section id="pricing-tool" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-5xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Paste Product Link for Instant Pricing
          </h2>
          <p className="text-gray-500 text-lg">
            Supports Amazon India (amazon.in, amzn.in) product pages
          </p>
        </div>

        {/* URL input card */}
<div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-10 border border-gray-100 shadow-sm">
  <div className="flex flex-col sm:flex-row items-center sm:space-x-16">  {/* Increased gap from 3 to 4 */}
    <div className="relative flex-1">  {/* Use flex-1 to let it take available space */}
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none z-10" />
      <input
        type="url"
        placeholder="Paste Amazon India link (including short amzn.in links)..."
        value={url}
        onChange={(e) => { setUrl(e.target.value); setError(""); }}
        onKeyDown={(e) => e.key === "Enter" && fetchProduct()}
        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-sm"
      />
    </div>
    <button
      onClick={fetchProduct}
      disabled={loading}
      className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl transition whitespace-nowrap shadow-sm min-h-[48px]"
    >
      {loading ? (
        <><Loader2 className="h-4 w-4 animate-spin" /> Fetching…</>
      ) : (
        "Fetch Product"
      )}
    </button>
  </div>

  {error && (
    <div className="mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
      <p className="text-red-700 text-sm">{error}</p>
    </div>
  )}
</div>  

        {/* Skeleton */}
        {loading && (
          <div className="rounded-3xl border border-gray-100 shadow-lg overflow-hidden animate-pulse">
            <div className="p-8 flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-56 h-56 bg-gray-200 rounded-2xl flex-shrink-0" />
              <div className="flex-1 space-y-4">
                <div className="h-7 bg-gray-200 rounded w-3/4" />
                <div className="h-5 bg-gray-100 rounded w-1/2" />
                <div className="h-5 bg-gray-100 rounded w-1/3" />
                <div className="h-20 bg-gray-100 rounded-xl mt-6" />
              </div>
            </div>
          </div>
        )}

        {/* Product card */}
        {productData && !loading && (
          <div className="rounded-3xl border border-gray-100 shadow-xl overflow-hidden bg-white">
            <div className="p-6 md:p-8">
              {/* Source badge */}
              <div className="flex justify-between items-center mb-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  {productData.source}
                </span>
              </div>

              <div className="flex flex-col lg:flex-row gap-8">
                {/* Image */}
                <div className="flex-shrink-0 flex justify-center">
                  {productData.imageUrl ? (
                    <img
                      src={productData.imageUrl}
                      alt={productData.name}
                      className="w-48 h-48 lg:w-56 lg:h-56 object-contain rounded-2xl bg-gray-50 border border-gray-100 p-4"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/240x240/F3F4F6/9CA3AF?text=No+Image";
                      }}
                    />
                  ) : (
                    <div className="w-48 h-48 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 text-sm">
                      No Image
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 leading-snug mb-3">
                      {productData.name}
                    </h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                      {productData.brand && (
                        <span>Brand: <strong className="text-gray-800">{productData.brand}</strong></span>
                      )}
                      {productData.model && (
                        <span>Model: <strong className="text-gray-800">{productData.model}</strong></span>
                      )}
                      <span>Weight: <strong className="text-gray-800">~{productData.weight} kg</strong></span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 bg-gray-50 rounded-2xl p-5">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Package className="h-4 w-4 text-gray-500" />
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={qty}
                        onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none transition text-sm"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Truck className="h-4 w-4 text-gray-500" />
                        Delivery Type
                      </label>
                      <select
                        value={deliveryType}
                        onChange={(e) => setDeliveryType(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none transition text-sm"
                      >
                        <option value="normal">Standard (7–10 Days) — Best Value</option>
                        <option value="express">Express (48 Hours) — Priority Shipping</option>
                      </select>
                    </div>
                  </div>

                  {/* Delivery note */}
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-gray-500" />
                      Delivery:{" "}
                      <strong className="text-gray-800">
                        {deliveryType === "express" ? "48 hours (Express)" : "7–10 business days"}
                      </strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Price Section */}
              {pricing && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
                    {/* Our Price (excl. GST) */}
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">
                        our Price  ({qty} unit{qty > 1 ? "s" : ""})
                      </p>
                      <div className="text-4xl font-bold text-blue-600">
                        ₹{formatINR(pricing.finalPrice)}
                      </div>
                      {qty > 1 && (
                        <p className="text-sm text-gray-500 mt-1">
                          ₹{formatINR(pricing.perUnit)} per unit
                        </p>
                      )}
                      {/* {pricing.discountPct > 0 && (
                        <p className="text-xs text-emerald-600 font-semibold mt-1">
                          {pricing.discountPct}% discount applied
                          {deliveryType === "express" ? " + express shipping" : ""}
                        </p>
                      )} */}
                    </div>

                    {/* Amazon Price (with GST, strikethrough) */}
                    <div className="text-left sm:text-right flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">
                        Amazon Price  (Total)
                      </p>
                      <div className="text-2xl text-gray-400 line-through">
                        ₹{formatINR(productData.price * qty)}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        (₹{formatINR(productData.price)} × {qty})
                      </p>
                    </div>
                  </div>

                  {/* Classification badges — helpful context */}
                  {/* <div className="flex gap-2 mt-4 flex-wrap">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      pricing.isLight
                        ? "bg-blue-50 text-blue-700 border-blue-100"
                        : "bg-orange-50 text-orange-700 border-orange-100"
                    }`}>
                      {pricing.isLight ? "Light (≤2 kg)" : "Heavy (>2 kg)"}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      pricing.isLowPrice
                        ? "bg-green-50 text-green-700 border-green-100"
                        : "bg-purple-50 text-purple-700 border-purple-100"
                    }`}>
                      {pricing.isLowPrice ? "Under ₹50K" : "Above ₹50K"}
                    </span>
                    {deliveryType === "express" && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-red-50 text-red-700 border-red-100">
                        Express +₹{formatINR(pricing.expressShippingCost)} shipping
                      </span>
                    )}
                  </div> */}
                </div>
              )}
            </div>

            {/* Add to cart button */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-[2px]">
              <button
                onClick={handleAddToCart}
                className="w-full bg-white hover:bg-gray-50 text-blue-700 font-bold py-5 text-lg flex items-center justify-center gap-3 transition-all duration-200"
              >
                {added ? (
                  <><CheckCircle className="h-5 w-5 text-emerald-500" /> Added to Cart!</>
                ) : (
                  <><ShoppingCart className="h-5 w-5" /> Add to Quote &amp; Unlock Best Pricing</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductLinkPricing;